import { anonymousKeyFor, rememberAnonymousKey, setPendingKey } from "@/lib/dany";
import { API } from "@/lib/api";

/**
 * Dany's voice channel, browser side (DY1.6).
 *
 * The whole flow is: ask our backend for permission, get a single-use signed
 * WebSocket URL back, and talk directly to ElevenLabs over it. The API key never
 * reaches this file — `POST /api/voice/session` is the only thing that can mint
 * a conversation, which is what makes the minute budget an actual gate.
 *
 * **Written against the raw WebSocket protocol rather than @elevenlabs/client.**
 * The SDK would be a new npm dependency, and a dependency means touching
 * yarn.lock — which, with a local node_modules that cannot be trusted, is a
 * change nobody here can validate before Railway builds it. The protocol is
 * small: send `user_audio_chunk` up, play `audio` events down, answer `ping`
 * with `pong`. Both directions are 16 kHz mono PCM, which is what the agent is
 * configured for (`pcm_16000`), so there is no resampling contract to get wrong
 * beyond the browser's own capture rate.
 *
 * The caller gets one object with `stop()` and a stream of events; every piece
 * of UI state the thread screen shows comes from those events.
 */

/** 16 kHz, mono — matches the agent's `user_input_audio_format`. */
const SAMPLE_RATE = 16000;
/** ~64 ms per chunk. Small enough that interruptions feel immediate. */
const FRAME_SIZE = 1024;

/**
 * Above this RMS the caller is talking. Used for the two things the surface
 * cannot learn from the socket in time:
 *
 *   - the moment speech ENDS, which is when Dany should look like she is
 *     thinking. `user_transcript` is the socket's answer to the same question
 *     and it arrives seconds later — measured at ~4s — so waiting for it would
 *     leave the caller staring at a listening face long after they stopped.
 *   - whether the caller is still there, which drives the countdown ring.
 *
 * Never used to decide what is SENT. The agent runs its own turn detection over
 * a continuous stream, and a client that withheld frames it judged silent would
 * be making that decision twice, worse, and from one side.
 */
const VOICED_RMS = 0.012;
/** Silence this long counts as "they stopped talking", not "they paused". */
const END_OF_SPEECH_MS = 600;

/**
 * ElevenLabs refuses a conversation by closing the socket with a 4xxx code.
 *
 * **The close frame is the only place the reason exists.** A refusal arrives as
 * a clean close — no `error` event on the socket first — so a handler that
 * treats every close as "the call ended" reports a finished conversation that
 * never started. That is exactly what made a hit quota look like a bug: the
 * surface went to "Connecting…", the socket closed a second later, and the
 * screen returned to idle with nothing said. The caller's only available move
 * was to press it again, which is what a loop of empty conversations looks
 * like from the logs.
 *
 * 4300 is the agent's daily call limit, which is a real operating ceiling
 * rather than a fault, so it gets its own message. Any other 4xxx is reported
 * as a failure to connect — still vague, but honestly vague, and with the code
 * logged for whoever reads it next. Codes below 4000 are left alone: a normal
 * hangup and a dropped network are already handled as the end of a call.
 */
const REFUSAL_CODES = { 4300: "voice_daily_limit" };

// The last-resort value for `fixy_location`, used only if a session response
// arrives without one. It mirrors `services.dany.location.VOICE_LOCATION_UNKNOWN`
// and exists because the variable must be sent on every conversation: the
// agent's prompt references it, and an unsupplied reference fails the call
// rather than falling back to anything. Degrading into asking for the ZIP code
// is the honest failure; the dishonest one would be asserting a place.
const LOCATION_UNKNOWN =
  "You do not know where this caller is, and find_fixybees ranks by distance. " +
  "When a search needs a location, ask for their ZIP code in one short " +
  "question, say the digits back to check you heard them, and search on " +
  "exactly those. Never choose a ZIP code for them.";

// The same last-resort role for `fixy_job` (R9), mirroring
// `services.dany.job_context.VOICE_JOB_NONE`. Sent whenever the session says
// this conversation has no job, which is the great majority of calls — the
// agent's prompt references `{{fixy_job}}` and an unsupplied reference is
// refused, not defaulted, so there is no version of this that can be omitted.
//
// It is an instruction NOT to mention a job, not an empty string: the prompt
// puts it under the heading "THE JOB THIS CALL IS ABOUT", and a blank there is
// an invitation to fill it in.
// The last-resort value for `fixy_today`. Not a date on purpose — see the
// comment where it is sent. Mirrors `agent_sync.TODAY_PLACEHOLDER`.
const TODAY_UNKNOWN = "unknown";

const JOB_NONE =
  "This call was not opened from any particular job, so there is no job on " +
  "the table yet. Do not mention one, do not imply the caller is quoting " +
  "anything, and find out what they need the ordinary way.";

// The last-resort value for `fixy_who` (T11 N51), mirroring
// `services.dany.identity.VOICE_WHO_UNKNOWN`. Sent unconditionally like the
// three above it and for the same reason: the agent's prompt references
// `{{fixy_who}}`, and an unsupplied reference is refused on arrival rather
// than defaulted.
//
// The honest unknown rather than a blank, because it sits under the heading
// "WHO YOU ARE TALKING TO" — and because it has to degrade into the behaviour
// that was correct before this variable existed. What it replaces was a static
// paragraph telling the model to assume nothing about the caller, which was
// right for a visitor and wrong for everybody else.
const WHO_UNKNOWN =
  "You do not know yet whether this person has a FixyDany account, or " +
  "whether they are a client or a FixyBee. Do not assume, and do not ask " +
  "them to identify themselves — a tool that needs an account will say so, " +
  "and that is the answer.";

// What the screen can take in (T19 F6), when the server sent nothing — the
// mirror of `services.dany.surface.VOICE_SURFACE_UNKNOWN`. The safe reading:
// no photos. Sent unconditionally like the four above, because the agent's
// prompt references `{{fixy_surface}}` from 1.39.0 on.
// The marker `services.dany.photos.voice_nudge` puts on the caller's photo line.
const PHOTO_NUDGE = /\(ref [0-9a-f]{6}\)\.?$/i;

const SURFACE_UNKNOWN =
  "text: yes; voice: yes; attachments (photos): no.\n" +
  "You do not know whether this caller can attach photos, so do not offer it.";

function refusalFrom(event) {
  const code = event?.code;
  const reason = event?.reason ? `: ${event.reason}` : "";
  return {
    code: REFUSAL_CODES[code] || "voice_connect_failed",
    message: `socket closed ${code == null ? "?" : code}${reason}`,
  };
}

/**
 * Resample a mono Float32 frame to 16 kHz.
 *
 * **Why this is not optional.** `new AudioContext({sampleRate: 16000})` is a
 * request, not a guarantee — plenty of browsers and devices hand back their
 * hardware rate, usually 48000, and the original version of this file noted
 * that in a comment and then sent the frames anyway. The agent is configured
 * for `pcm_16000` and there is no way to tell it otherwise at connection time,
 * so three samples arrived for every one it expected: one second of speech
 * became three seconds of audio.
 *
 * Measured against the real agent by replaying the same recorded sentence at
 * both rates: at 16 kHz the transcript came back as "How much do the FixyBee
 * plans cost per month?", and at 48 kHz mislabelled as 16 kHz it came back as
 * "...". Not slower — unintelligible.
 *
 * Linear interpolation between two real neighbours, always. The samples a frame
 * ends on are carried into the next call rather than interpolated against
 * themselves — an interpolation that clamps at the frame edge introduces a tiny
 * step, and at 47 frames a second that is 47 steps a second of broadband click.
 * A first version did exactly that; a 440 Hz tone came back reading 459 Hz
 * because the counter was seeing the edge artefacts as extra zero crossings.
 * Sample counts were correct throughout, which is the tell: the rate was right
 * and the waveform was not.
 */
function createResampler(inputRate) {
  const ratio = inputRate / SAMPLE_RATE;
  // Fractional read index into `carry ++ input`, and the tail of the previous
  // frame that the next one has to interpolate from.
  let position = 0;
  let carry = new Float32Array(0);
  return (input) => {
    if (ratio === 1) return input;
    const work = new Float32Array(carry.length + input.length);
    work.set(carry);
    work.set(input, carry.length);

    // Produce only while `low + 1` is a real sample of `work`.
    const outLength = Math.max(
      0, Math.floor((work.length - 1 - position) / ratio) + 1
    );
    const output = new Float32Array(outLength);
    for (let i = 0; i < outLength; i += 1) {
      const at = position + i * ratio;
      const low = Math.floor(at);
      const fraction = at - low;
      output[i] = work[low] * (1 - fraction) + work[low + 1] * fraction;
    }

    const next = position + outLength * ratio;
    const consumed = Math.floor(next);
    carry = work.slice(consumed);
    position = next - consumed;
    return output;
  };
}

/**
 * Ask the backend to authorise a voice conversation.
 *
 * Throws an Error carrying `code` so the UI can tell "sign in" from "you're out
 * of minutes" from "it broke", which are three different things to say.
 */
export async function requestVoiceSession({ threadId, language, turnstileToken } = {}) {
  // The anonymous key travels the same way it does on the text path, in the
  // same header, and for the same reason: it is what proves this browser owns
  // the conversation it is asking to speak into. A signed-in caller sends none
  // and the cookie decides.
  const key = threadId ? anonymousKeyFor(threadId) : null;
  const response = await fetch(`${API}/voice/session`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(key ? { "X-Dany-Anonymous-Key": key } : {}),
    },
    body: JSON.stringify({
      thread_id: threadId || null,
      language: language || null,
      turnstile_token: turnstileToken || null,
    }),
  });
  if (!response.ok) {
    let detail = {};
    try {
      const body = await response.json();
      detail = typeof body.detail === "object" && body.detail ? body.detail : {};
    } catch {
      /* an HTML error page from a proxy — fall through to the status code */
    }
    const error = new Error(detail.message || `Voice session failed (${response.status})`);
    error.code = detail.code
      || (response.status === 401 ? "voice_sign_in_required"
        : response.status === 429 ? "voice_rate_limited"
        : `http_${response.status}`);
    error.allowanceMinutes = detail.allowance_minutes;
    throw error;
  }
  const session = await response.json();
  // A conversation the microphone started has no key until this reply carries
  // one. Storing it here is what lets the visitor reopen the call they just had
  // — the text path does the same on its `thread` frame.
  if (session.anonymous_key && session.thread_id) {
    setPendingKey(session.anonymous_key);
    rememberAnonymousKey(session.thread_id, session.anonymous_key);
  }
  return session;
}

/**
 * The widget payloads the call has produced so far.
 *
 * The socket says a tool finished and names it; it never says what came back.
 * `agent_tool_response` is `{tool_name, tool_call_id, tool_type, is_error,
 * is_blocked, event_id, is_called}` — verified against the live agent, not
 * assumed — because `run_tool` returns only the spoken summary to ElevenLabs on
 * purpose. The structured data stays on our side, so the card comes from here.
 *
 * Returns `[]` rather than throwing on any failure. A missing card is a call
 * that looks like it did before; an exception on this path would be a call that
 * ends.
 */
export async function fetchVoiceToolPayloads(threadId) {
  if (!threadId) return [];
  // The key travels for the same reason it travels on every other Dany read: a
  // visitor with no account owns this conversation through it and through
  // nothing else. Sending only the cookie is what made this the one voice
  // endpoint an anonymous caller could not reach, and a 401 here is silent —
  // the catch below turns it into "no cards yet", which looks exactly like a
  // call where no tool has run.
  const key = anonymousKeyFor(threadId);
  try {
    const response = await fetch(
      `${API}/voice/threads/${encodeURIComponent(threadId)}/tools`,
      {
        credentials: "include",
        headers: key ? { "X-Dany-Anonymous-Key": key } : undefined,
      },
    );
    if (!response.ok) return [];
    const body = await response.json();
    return Array.isArray(body.tools) ? body.tools : [];
  } catch {
    return [];
  }
}

/**
 * A spoken turn, as a reader should see it.
 *
 * A multi-language agent wraps what it hands to TTS in a language tag —
 * `<Spanish>…</Spanish>` — and the same string arrives here as `agent_response`.
 * It belongs to the audio pipeline, not to the transcript, so it is removed at
 * the two places the text becomes readable: here for the live turn, and in the
 * post-call webhook for the stored one (backend `services/voice/transcript.py`,
 * same pattern and same reasoning).
 *
 * Narrow on purpose: a capitalised single word in brackets is the shape of
 * every language tag and of nothing a caller says. Stripping anything
 * tag-shaped would also eat `<3` or a dictated email address.
 */
const LANGUAGE_TAG = /<\/?(?:[A-Z][a-z]{2,15}|default)>/g;

function cleanSpokenText(text) {
  return String(text || "").replace(LANGUAGE_TAG, "").replace(/\s{2,}/g, " ").trim();
}

/** Float32 [-1,1] from the mic → the little-endian 16-bit PCM the agent wants. */
function floatToPcm16(input) {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    // Clamp before scaling: a sample above 1.0 (which happens on loud input
    // after gain) would wrap to a large negative number and arrive as a click.
    const sample = Math.max(-1, Math.min(1, input[i]));
    output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return output;
}

function frameRms(input) {
  let sum = 0;
  for (let i = 0; i < input.length; i += 1) sum += input[i] * input[i];
  return Math.sqrt(sum / (input.length || 1));
}

function bytesToBase64(bytes) {
  let binary = "";
  const chunk = 0x8000;
  // String.fromCharCode(...huge) blows the argument limit on a long buffer, so
  // the array is walked in slices.
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return window.btoa(binary);
}

function base64ToInt16(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Int16Array(bytes.buffer);
}

/**
 * Plays the agent's audio in order, and can drop everything on an interruption.
 *
 * A queue rather than "play each chunk as it arrives": chunks arrive faster
 * than real time, and scheduling each one at `currentTime` would overlap them
 * into noise. Each buffer is scheduled where the previous one ends, so the
 * speech is continuous, and `flush()` exists because when the user interrupts,
 * everything already scheduled has to stop — otherwise Dany keeps talking over
 * them for as long as the queue is deep.
 */
class Playback {
  constructor(context) {
    this.context = context;
    this.cursor = 0;
    this.sources = new Set();
    this.onStateChange = null;
  }

  get speaking() {
    return this.sources.size > 0;
  }

  push(int16, sampleRate) {
    const buffer = this.context.createBuffer(1, int16.length, sampleRate);
    const channel = buffer.getChannelData(0);
    for (let i = 0; i < int16.length; i += 1) channel[i] = int16[i] / 0x8000;

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.context.destination);

    const now = this.context.currentTime;
    // A gap in delivery leaves the cursor in the past; restarting from `now`
    // keeps the next word immediate instead of replaying a backlog late.
    const startAt = Math.max(now, this.cursor);
    source.start(startAt);
    this.cursor = startAt + buffer.duration;

    this.sources.add(source);
    const wasSpeaking = this.sources.size > 1;
    if (!wasSpeaking) this.onStateChange?.(true);
    source.onended = () => {
      this.sources.delete(source);
      if (!this.sources.size) this.onStateChange?.(false);
    };
  }

  flush() {
    for (const source of this.sources) {
      try { source.stop(); } catch { /* already ended */ }
    }
    this.sources.clear();
    this.cursor = 0;
    this.onStateChange?.(false);
  }
}

/**
 * Open a full-duplex voice conversation.
 *
 * `onEvent(name, data)` receives, in the order they happen:
 *
 *   status        {state: "connecting"|"listening"|"speaking"|"ended"}
 *   user          {text}          a finished transcription of the caller
 *   agent         {text}          something Dany said
 *   agentCorrection {text}        Dany's own revision of her last line
 *   error         {code, message}
 *
 * Returns `{ stop }`. Calling `stop()` is safe at any point, including before
 * the socket opens.
 */
export function startVoiceConversation({ session, onEvent }) {
  let socket = null;
  let audioContext = null;
  let mediaStream = null;
  let processor = null;
  let sourceNode = null;
  let playback = null;
  let stopped = false;
  // Whether the conversation ever got going. Separates "the call ended" from
  // "the call was refused", which arrive as the same close event.
  let opened = false;
  // Local voice-activity state. See VOICED_RMS.
  let speaking = false;
  let lastVoicedAt = 0;

  const emit = (name, data) => { if (!stopped || name === "status") onEvent?.(name, data); };

  const teardown = () => {
    if (stopped) return;
    stopped = true;
    try { processor?.disconnect(); } catch { /* already gone */ }
    try { sourceNode?.disconnect(); } catch { /* already gone */ }
    // Stopping the tracks is what turns the browser's recording indicator off.
    // Leaving them live after a call is over reads as the site still listening,
    // and for a voice assistant that is the one impression worth spending code
    // to avoid.
    mediaStream?.getTracks().forEach((track) => track.stop());
    playback?.flush();
    try { audioContext?.close(); } catch { /* already closed */ }
    if (socket && socket.readyState <= WebSocket.OPEN) socket.close();
    onEvent?.("status", { state: "ended" });
  };

  (async () => {
    emit("status", { state: "connecting" });
    try {
      mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          // The agent runs its own turn detection over the raw stream; the
          // browser's echo cancellation is still wanted, because without it a
          // laptop speaker playing Dany is heard by the mic as the user talking
          // and she interrupts herself.
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      emit("error", {
        code: err?.name === "NotAllowedError" ? "voice_mic_denied" : "voice_mic_failed",
        message: String(err),
      });
      teardown();
      return;
    }
    if (stopped) { mediaStream.getTracks().forEach((t) => t.stop()); return; }

    // Ask for 16 kHz directly. Browsers may hand back their hardware rate
    // anyway, which is why the actual `audioContext.sampleRate` is what gets
    // sent — the agent is told the rate it was configured with and a mismatch
    // would make everything sound slow or fast rather than fail loudly.
    audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: SAMPLE_RATE,
    });
    playback = new Playback(audioContext);
    playback.onStateChange = (speaking) => {
      if (!stopped) onEvent?.("status", { state: speaking ? "speaking" : "listening" });
    };

    socket = new WebSocket(session.signed_url);

    socket.onopen = () => {
      opened = true;
      // Two dynamic variables, both server-supplied. The grant is what every
      // tool call will be authenticated by. The date is what the agent's prompt
      // resolves "next week" against — it comes from the server rather than
      // from this browser's clock, so the two Dany channels agree about what
      // day it is and a device with a wrong clock cannot post-date a job.
      // `conversation_config_override` is limited to the language, which is the
      // single override the agent allows.
      socket.send(JSON.stringify({
        type: "conversation_initiation_client_data",
        dynamic_variables: {
          [session.grant_variable || "fixy_grant"]: session.grant,
          // **Unconditional, like the two below it** (R10, closing AUDIT.md
          // §54.5). This was a conditional spread until now — the same shape
          // R8 removed from `fixy_location` and flagged here as the identical
          // latent bug. The agent's prompt references `{{fixy_today}}`, and a
          // referenced variable that is not supplied does not fall back to the
          // placeholder registered on the agent: it fails the conversation on
          // arrival. So a session response without a date would not have
          // degraded the call, it would have ended it before the first word.
          //
          // The fallback is deliberately not a date. `_TODAY` in the prompt
          // ends with "if the line above does not name a real date, you do not
          // know what day it is: ask" — so "unknown" degrades into asking,
          // which is safe, while a stale date would be confidently wrong. It
          // mirrors `agent_sync.TODAY_PLACEHOLDER`.
          [session.today_variable || "fixy_today"]: session.today || TODAY_UNKNOWN,
          // Where the caller is, computed server-side by
          // `voice_location_value` — the thread's remembered ZIP, else the
          // signed-in caller's profile, else the honest "you do not know".
          //
          // **Sent unconditionally**, unlike the date above. The agent's prompt
          // references `{{fixy_location}}`, and a referenced variable that is
          // not supplied does not fall back to the placeholder registered on
          // the agent — ElevenLabs documents those as defaults for testing, not
          // for live conversations, and a missing one fails the conversation on
          // arrival. Omitting this key would therefore not degrade the call, it
          // would end it before the first word.
          [session.location_variable || "fixy_location"]:
            session.location || LOCATION_UNKNOWN,
          // What this call is about, when the thread has a subject (R9). Null
          // from the server is the ordinary case and means exactly that — see
          // `voice_job_value` — so the fallback here is not a degradation, it
          // is the honest value for a call opened from no job. Sent
          // unconditionally for the reason above it.
          [session.job_variable || "fixy_job"]: session.job || JOB_NONE,
          // Who is on the line (T11 N51). Computed server-side from the live
          // session rather than from this browser's cached profile: the
          // backend already knows which account the cookie resolves to, and a
          // browser holding a stale document would be the second source of
          // truth this variable exists to remove. Unconditional for the reason
          // the three above it are.
          [session.who_variable || "fixy_who"]: session.who || WHO_UNKNOWN,
          // What this screen can take in — whether a photo can be attached
          // during the call, and the gesture (T19 F6). Unconditional for the
          // reason the four above are.
          [session.surface_variable || "fixy_surface"]:
            session.surface || SURFACE_UNKNOWN,
        },
        conversation_config_override: {
          agent: { language: session.language === "es" ? "es" : "en" },
        },
      }));

      // **And the same fact again, this time inside the conversation** (R8).
      //
      // The system prompt is not where a fact the model must act on THIS turn
      // survives. `orchestrator.py` learned that on the text channel and says
      // so where it appends the location block: "a fact the model has to act on
      // THIS turn does not survive being placed upstream of everything the user
      // said". R4 gave the voice channel the same fact and put it exactly
      // there — in 29,636 characters of static prompt — and production answered
      // on 2026-09-11 22:26 and 22:27: the variable arrived carrying El Paso
      // and 79936, the agent ran the synced 1.27.0 prompt, and Dany asked for
      // the ZIP code anyway, twice.
      //
      // A contextual update lands in the conversation history at the point it
      // arrives, not in the system prompt, and does not prompt the agent to
      // speak. That is the text channel's placement, reached by the one
      // mechanism this channel has. Sent immediately after the initiation
      // frame: the socket preserves order, so the conversation exists by the
      // time this is read, and both are ahead of the caller's first word.
      //
      // `context_id` so that a later update about the same subject supersedes
      // this one rather than stacking a second, contradictory location into the
      // history.
      socket.send(JSON.stringify({
        type: "contextual_update",
        context_id: "fixy_location",
        text: session.location || LOCATION_UNKNOWN,
      }));

      // **And the job, on exactly the same terms** (R9).
      //
      // R7 tied the job to the thread server-side and said the tie "serves both
      // channels". It does — `propose_quote` with no `job_id` lands on
      // `dany_threads.about_job_id` from either — but the tie is only half of
      // it: the model still has to know there is a subject before it will build
      // line items instead of asking which job. Production proved the gap on
      // 2026-09-12 in dthr_942464352f54, one thread carrying both channels:
      // seq 4, by text, bound the draft to job_6c721d7abf9b; seq 13, by voice,
      // eighty seconds later, was "¿Podrías decirme el ID del trabajo o el
      // título?".
      //
      // The text turn passed the id explicitly — `{"job_id":
      // "job_6c721d7abf9b", "line_items": [...]}` — because `job_block` had
      // put it in front of the model. That is the point rather than a
      // footnote: R7's server-side fallback is not what made text work, being
      // TOLD is, and voice was told nothing.
      //
      // **Only when there is one.** The dynamic variable above goes out on
      // every call because the prompt references it; this does not, because a
      // contextual update is an event in the conversation's own history and
      // "there is no job here" written into the history of a call that never
      // had one is how a caller gets told about a job that does not exist.
      // Silence is the correct default for this delivery, and the witness case
      // for it is a thread with no subject: Dany must not mention a job at all.
      if (session.job) {
        socket.send(JSON.stringify({
          type: "contextual_update",
          context_id: "fixy_job",
          text: session.job,
        }));
      }

      // **And who is on the line, on the terms of the location rather than the
      // job** (T11 N51).
      //
      // Unconditional, like `fixy_location` and unlike `fixy_job`. The test is
      // not "is this interesting" but "is silence the honest default", and
      // here it is not: every call has somebody on it, and the no-account
      // sentence is an instruction the model needs as much as the signed-in
      // one — it is what keeps a visitor from being asked to identify
      // themselves. The job's silence is correct because a call with no job
      // has no subject to describe; a call with no account still has a caller.
      //
      // And the second delivery is the one that works. R8 measured the system
      // prompt losing `fixy_location` to everything the caller said after it,
      // on a call running the synced prompt with the right value in it; this
      // fact is read at exactly the moment R8's was — the first turn, when
      // somebody asks Dany who they are.
      socket.send(JSON.stringify({
        type: "contextual_update",
        context_id: "fixy_who",
        text: session.who || WHO_UNKNOWN,
      }));

      // And what the screen can take in (T19 F6), on the terms of the caller:
      // unconditional, because "this caller cannot attach photos" is as much an
      // instruction as the opposite — it is what keeps Dany from asking for one.
      socket.send(JSON.stringify({
        type: "contextual_update",
        context_id: "fixy_surface",
        text: session.surface || SURFACE_UNKNOWN,
      }));

      // **And the conversation so far, when there is one** (N37).
      //
      // Third delivery on exactly the terms of the second. The text channel
      // replays the transcript every turn, so a thread spoken and then typed
      // carries on; this channel replayed nothing, and a second call on the
      // same thread opened at the agent's first_message with no idea the
      // first had happened. Production 2026-09-19, dthr_b10bea19068a: the
      // caller said what they needed, said "dame un momento", hung up, came
      // back five minutes later and was greeted as a stranger.
      //
      // Only when there is one, and that is the witness case rather than an
      // optimisation: a recap is an event in the conversation's own history,
      // and telling a first-time caller what they said before is worse than
      // telling them nothing. Which is why the server returns null for a
      // fresh thread and this is a contextual update rather than a dynamic
      // variable — a variable the prompt references and does not receive ends
      // the conversation on arrival.
      if (session.resume) {
        socket.send(JSON.stringify({
          type: "contextual_update",
          context_id: "fixy_resume",
          text: session.resume,
        }));
      }

      // **And what the quote draft holds, when this call is about one** (T16 M1).
      //
      // Fourth delivery on exactly the terms of the job and the recap. The text
      // channel appends `quote_draft_block` to every FixyBee turn; this channel
      // was never told what the draft contained, and on 2026-09-23 a FixyBee
      // dictated line items out loud while Dany answered "listo, lo agregué"
      // and the card on screen stayed empty — no `propose_quote` had run, and
      // nothing in the conversation contradicted the model's own memory of
      // having recorded them.
      //
      // Only when there is something to say, which is the witness case rather
      // than an optimisation: a client's call has no quote draft by
      // construction, and writing "there is no quote draft" into the history of
      // a call that was never about quoting is how a caller gets told about an
      // object that does not concern them. The server returns null for every
      // such call.
      //
      // This is the state at the moment the call opened, and it is all this
      // delivery can be — nothing can send a second update into a call in
      // progress from here. What corrects the draft mid-call is
      // `propose_quote`'s own result, and the tool's description is what makes
      // it get called.
      if (session.draft) {
        socket.send(JSON.stringify({
          type: "contextual_update",
          context_id: session.draft_variable || "fixy_draft",
          text: session.draft,
        }));
      }

      // The rate we were actually given, which is not necessarily the one asked
      // for. Logged when it differs, because a device that resamples is the one
      // detail that explains a call sounding wrong to someone reporting it.
      const captureRate = audioContext.sampleRate;
      const resample = createResampler(captureRate);
      if (captureRate !== SAMPLE_RATE) {
        console.info(
          `[dany voice] capture is ${captureRate} Hz, resampling to ${SAMPLE_RATE} Hz`
        );
      }

      sourceNode = audioContext.createMediaStreamSource(mediaStream);
      // ScriptProcessorNode is deprecated in favour of AudioWorklet, and is used
      // anyway: an AudioWorklet needs its processor loaded from a separate URL,
      // which CRA would have to be taught to emit as its own asset. This node is
      // still supported everywhere the app runs, and the work it does per frame
      // is a clamp and a multiply.
      processor = audioContext.createScriptProcessor(FRAME_SIZE, 1, 1);
      processor.onaudioprocess = (event) => {
        if (stopped || socket.readyState !== WebSocket.OPEN) return;
        const captured = event.inputBuffer.getChannelData(0);

        // Voicedness is read from the captured frame, before resampling: the
        // question is whether a person is making sound, and resampling does not
        // change the answer.
        const now = performance.now();
        if (frameRms(captured) > VOICED_RMS) {
          if (!speaking) {
            speaking = true;
            emit("speech", { active: true });
          }
          lastVoicedAt = now;
        } else if (speaking && now - lastVoicedAt > END_OF_SPEECH_MS) {
          speaking = false;
          emit("speech", { active: false });
        }

        const frame = resample(captured);
        if (!frame.length) return;
        const pcm = floatToPcm16(frame);
        socket.send(JSON.stringify({
          user_audio_chunk: bytesToBase64(new Uint8Array(pcm.buffer)),
        }));
      };
      sourceNode.connect(processor);
      // A ScriptProcessorNode only fires while it is connected to the graph's
      // destination. Routing it through a muted gain node is the standard way to
      // satisfy that without the caller hearing their own microphone.
      const silence = audioContext.createGain();
      silence.gain.value = 0;
      processor.connect(silence);
      silence.connect(audioContext.destination);

      onEvent?.("status", { state: "listening" });
    };

    socket.onmessage = (message) => {
      let event;
      try {
        event = JSON.parse(message.data);
      } catch {
        return;
      }
      switch (event.type) {
        case "audio":
          playback.push(
            base64ToInt16(event.audio_event?.audio_base_64 || ""),
            audioContext.sampleRate,
          );
          break;
        case "user_transcript": {
          const said = cleanSpokenText(event.user_transcription_event?.user_transcript);
          // T19 F5. The line `sendUserMessage` sent with a photo ("I sent you a
          // photo (ref 3f9a2c).") is a delivery, not something the caller
          // said; if the agent echoes it back as a transcript, the photo's own
          // thumbnail is already on screen in its place.
          if (PHOTO_NUDGE.test(said || "")) break;
          emit("user", { text: said });
          break;
        }
        case "agent_response":
          emit("agent", {
            text: cleanSpokenText(event.agent_response_event?.agent_response),
          });
          break;
        case "agent_response_correction":
          emit("agentCorrection", {
            text: cleanSpokenText(
              event.agent_response_correction_event?.corrected_agent_response),
          });
          break;
        case "agent_tool_request":
          // Which tool, so the surface can name it. The agent knows; the
          // alternative is the client inferring it from the transcript after
          // the fact, which is both late and a guess.
          emit("tool", {
            name: event.agent_tool_request_event?.tool_name
              || event.agent_tool_request?.tool_name || "",
            running: true,
          });
          break;
        case "agent_tool_response":
          emit("tool", { name: "", running: false });
          break;
        case "interruption":
          // The caller started talking. Everything queued is now stale — they
          // are answering what Dany said ten words ago, not what is still
          // scheduled to play.
          playback.flush();
          break;
        case "ping":
          socket.send(JSON.stringify({
            type: "pong", event_id: event.ping_event?.event_id,
          }));
          break;
        default:
          break;
      }
    };

    socket.onerror = () => {
      // Before the socket opened this is a failure to connect, and saying "the
      // connection dropped, your transcript is saved" about a call that never
      // happened is a claim about a transcript that does not exist.
      emit("error", opened
        ? { code: "voice_connection_lost", message: "socket error" }
        : { code: "voice_connect_failed", message: "socket error before open" });
    };

    socket.onclose = (event) => {
      // A refusal is a close with a 4xxx code and no `error` event before it.
      // ElevenLabs accepts the socket first — it has already created the
      // conversation by the time it decides the agent is over its daily limit —
      // so `opened` cannot tell a refusal from a hangup. The code can, and it
      // is the only description of what happened that ever reaches the browser.
      // Emitted before `teardown`, which sets `stopped` and closes `emit`.
      if (!stopped && Number(event?.code) >= 4000) {
        console.warn(`[dany voice] refused: ${event.code} ${event.reason || ""}`);
        emit("error", refusalFrom(event));
      }
      teardown();
    };
  })();

  /**
   * T19 F5 (Plan A of X6). Two frames into the LIVE call, in this order: the
   * photo's reading as a contextual update — a fact in the conversation's
   * history, superseded by the next photo under the same `context_id` (D6:
   * "this" is the last image) — and then the caller's line, as a
   * `user_message`, because a contextual update alone does not make the agent
   * speak and Plan A is Dany saying what she sees. Both are no-ops on a socket
   * that is not open.
   */
  const sendFrame = (frame) => {
    if (stopped || !socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(frame));
    return true;
  };
  const sendContext = (contextId, text) =>
    sendFrame({ type: "contextual_update", context_id: contextId, text });
  const sendUserMessage = (text) => sendFrame({ type: "user_message", text });

  return { stop: teardown, sendContext, sendUserMessage };
}
